import { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import type { Language } from "@/lib/site";
import { heroCopy } from "@/data/hero";
import { BRAND_TAGLINE } from "@/lib/brand";

type LanguageContextType = {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  en: {
    // Navigation
    home: "Home",
    about: "About",
    services: "Services",
    portfolio: "Portfolio",
    contact: "Contact",
    
    // Hero Section
    heroTitle: heroCopy.en.service,
    heroDescription: heroCopy.en.description,
    brandTagline: BRAND_TAGLINE.en,
    getStarted: "Get Started",
    viewOurWork: "Our Work",
    secure: "Quality",
    analytics: "Analytics",
    technology: "Creativity",
    scrollDown: "Scroll Down",
    heroImageAlt: "Dashboard preview",
    
    // About Section
    aboutTitle: "About",
    aboutTitleHighlight: "Ibtkary",
    aboutDescription: "We're a cutting-edge digital solutions company dedicated to transforming innovative ideas into powerful digital realities.",
    crossPlatformTitle: "Cross-Platform Excellence",
    crossPlatformDesc: "Leveraging Flutter for seamless mobile experiences across iOS and Android platforms.",
    backendTitle: "Robust Backend Solutions",
    backendDesc: "Powered by Go for high-performance, scalable backend architectures.",
    aiTitle: "AI-Powered Innovation",
    aiDesc: "Expanding into artificial intelligence to deliver next-generation digital solutions.",
    
    // Services Section
    servicesTitle: "Our",
    servicesTitleHighlight: "Services",
    servicesDescription: "Comprehensive digital solutions tailored to elevate your business in the modern digital landscape.",
    mobileAppDev: "Mobile App Development",
    mobileAppDesc: "Developing Android and iOS mobile applications with the best interfaces and user experience for all types of businesses.",
    backendDev: "Servers and Databases",
    backendDevDesc: "We provide a robust and secure infrastructure that ensures stable performance for your applications, so your systems operate efficiently and continuously without interruption.",
    webDev: "Web Development",
    webDevDesc: "Modern, responsive web applications that deliver exceptional user experiences across all devices and platforms.",
    aiSolutions: "AI Solutions",
    aiSolutionsDesc: "Next-generation artificial intelligence solutions to automate processes and unlock new possibilities for your business.",
    designBranding: "Design & Branding",
    designBrandingDesc: "From designing logos and colors to building a comprehensive identity that reflects your brand values ​​and makes it stand out in the market.",
    consulting: "Consulting Services",
    consultingDesc: "We provide consulting based on practical experience in developing systems and supervising project implementation to ensure quality and achieve the best results.",
    comingSoon: "Coming Soon",
    learnMore: "Learn More",
    
    // Portfolio Section
    portfolioTitle: "Our Portfolio",
    portfolioTitleHighlight: "",
    portfolioDescription: "our solutions for transforming ideas into digital reality.",
    rowaaqAcademy: "Rowaaq Academy Website",
    rowaaqAcademyDesc: "Modern professional website for an educational institution teaching programming and technology to kids aged 8-16.",
    nafeesJms: "Nafees JMS",
    nafeesJmsDesc: "Comprehensive SaaS platform with ERP, POS, and CRM for gold and silver jewelry stores.",
    nafees24: "Nafees 24",
    nafees24Desc: "Real-time gold, silver, and currency price tracking app for the Egyptian market.",
    discountLocker: "The Discount Locker",
    discountLockerDesc: "E-commerce mobile app for luxury brands at discounted prices, integrated with Shopify.",
    rowaaqLms: "Rowaaq LMS",
    rowaaqLmsDesc: "Comprehensive Learning Management System with gamification for student lifecycle management.",
    viewProject: "View Project",
    viewAllProjects: "View All Projects",
    duration: "Duration",
    teamSize: "Team Size",
    
    // Contact Section
    contactTitle: "Contact us to be",
    contactTitleHighlight: "Your success partners",
    contactDescription: "The perfect time to implement your idea is now. Contact our team and we'll turn your ideas into a successful project.",
    getInTouch: "Get In Touch",
    fullName: "Full Name",
    emailAddress: "Email Address",
    phoneNumber: "Phone Number",
    projectType: "Project Type",
    message: "Message",
    selectService: "Select a service",
    mobile: "Mobile App Development",
    web: "Web Development",
    backend: "Backend Development",
    ai: "AI Solutions",
    design: "Design & Branding",
    consultingShort: "Consulting",
    sendMessage: "Send Message",
    sending: "Sending...",
    emailUs: "Email Us",
    callUs: "Call Us",
    visitUs: "Visit Us",
    followUs: "Follow Us",
    namePlaceholder: "John Doe",
    emailPlaceholder: "john@example.com",
    phonePlaceholder: "+1 (555) 123-4567",
    messagePlaceholder: "Tell us about your project...",
    messageSentSuccess: "Message sent successfully!",
    messageSentDescription: "We'll get back to you within 24 hours.",
    messageFailedTitle: "Failed to send message",
    locationDetails1: "Mansoura, Egypt",
    locationDetails2: "King Fahd Road, Olaya District",
    
    // Footer
    footerServices: "Services",
    footerCompany: "Company",
    footerAboutUs: "About Us",
    footerPortfolio: "Portfolio",
    footerCareers: "Careers",
    footerContact: "Contact",
    footerBlog: "Blog",
    privacyPolicy: "Privacy Policy",
    termsOfService: "Terms of Service",
    allRightsReserved: "All rights reserved",
    footerDescription: "We specialize in cross-platform application development, back-end solutions, web development, and cutting-edge smart innovations that drive your business forward.",
    footerCopyright: "© 2025 Ibtkary. All rights reserved.",
    
    // Modal
    close: "Close",
    technologies: "Technologies",
    features: "Features",
    benefits: "Benefits",
    requestQuote: "Request a Quote",

    // Hero Trust Bar
    trustedBy: "Trusted by innovative companies",

    // Services CTA
    viewAllServices: "View All Services",

    // Newsletter
    newsletterTitle: "Subscribe to our Newsletter",
    newsletterDescription: "Get the latest news and updates delivered to your inbox",
    subscribe: "Subscribe",

    // Footer
    footerGetInTouch: "Get in Touch",
  },
  ar: {
    // Navigation Menu
    home: "الرئيسية",
    about: "من نحن",
    services: "خدماتنا",
    portfolio: "أعمالنا",
    contact: "تواصل معنا",
    
    // Hero Section
    heroTitle: heroCopy.ar.service,
    heroDescription: heroCopy.ar.description,
    brandTagline: BRAND_TAGLINE.ar,
    getStarted: "ابدأ معنا",
    viewOurWork: "أعمالنا",
    secure: "جـودة",
    analytics: "تحليلات",
    technology: "ابـداع",
    scrollDown: "قم بالتمرير للأسفل",
    heroImageAlt: "معاينة لوحة التحكم",
    
    // About Section
    aboutTitle: "عن",
    aboutTitleHighlight: "إبتكارى",
    aboutDescription: "نحن شركة حلول رقمية متطورة مكرسة لتحويل الأفكار المبتكرة إلى حقائق رقمية قوية.",
    crossPlatformTitle: "التميز متعدد المنصات",
    crossPlatformDesc: "الاستفادة من Flutter لتجارب الهاتف المحمول السلسة عبر منصات iOS و Android.",
    backendTitle: "حلول خلفية قوية",
    backendDesc: "مدعوم بـ Go للحصول على بنية خلفية عالية الأداء وقابلة للتطوير.",
    aiTitle: "الابتكار المدعوم بالذكاء الاصطناعي",
    aiDesc: "التوسع في الذكاء الاصطناعي لتقديم حلول رقمية من الجيل القادم.",
    
    // Services Section
    servicesTitle: "خدمات",
    servicesTitleHighlight: "إبتكاري",
    servicesDescription: "حلول رقمية شاملة بأحدث التقنيات و المعايير العالمية لضمان الجودة وافضل أداء وخدمات دعم فنى مستمرة لضمان نجاحك واستقرار مشروعك.",
    mobileAppDev: "تطوير تطبيقات الموبايل",
    mobileAppDesc: "تطوير تطبيقات الموبايل Android - IOS بأفضل الواجهات وتجربة المستخدم لكافة انواع الأعمال  ",
    backendDev: "الخوادم وقواعد البيانات",
    backendDevDesc: "نوفّر بنية تحتية قوية وآمنة تضمن أداءً مستقرًا لتطبيقاتك لتعمل أنظمتك بكفاءة واستمرارية عالية دون انقطاع.",
    webDev: "تطوير الويب",
    webDevDesc: "تطبيقات ويب حديثة ومتجاوبة تقدم تجارب مستخدم استثنائية عبر جميع الأجهزة والمنصات.",
    aiSolutions: "حلول الذكاء الاصطناعي",
    aiSolutionsDesc: "حلول الذكاء الاصطناعي من الجيل القادم لأتمتة العمليات وفتح إمكانيات جديدة لعملك.",
    designBranding: "التصميم والعلامة التجارية",
    designBrandingDesc: "من تصميم الشعارات والألوان إلى بناء هوية متكاملة تعكس قيم علامتك وتجعلها مميزة في السوق.",
    consulting: "خدمات الاستشارة",
    consultingDesc: "نقدم استشارات مبنية على خبرة عملية في تطوير الأنظمة والإشراف على تنفيذ المشاريع لضمان الجودة وتحقيق أفضل النتائج.",
    comingSoon: "قريباً",
    learnMore: "اعرف أكثر",
    
    // Portfolio Section
    portfolioTitle: "أعمالنا",
    portfolioTitleHighlight: "",
    portfolioDescription: "نماذج من حلولنا في تحويل الأفكار إلى واقع رقمي.",
    rowaaqAcademy: "موقع أكاديمية رُواق",
    rowaaqAcademyDesc: "موقع إلكتروني احترافي وعصري لمؤسسة تعليمية متخصصة في تعليم البرمجة والتكنولوجيا للأطفال من 8 إلى 16 سنة.",
    nafeesJms: "نفيس لإدارة محلات المجوهرات",
    nafeesJmsDesc: "منصة SaaS متكاملة تجمع بين ERP و POS و CRM لمحلات المجوهرات والذهب والفضة.",
    nafees24: "نفيس 24",
    nafees24Desc: "تطبيق تتبع أسعار الذهب والفضة والعملات اللحظية للسوق المصري.",
    discountLocker: "The Discount Locker",
    discountLockerDesc: "تطبيق تجارة إلكترونية للعلامات التجارية الفاخرة بأسعار مخفضة، متكامل مع Shopify.",
    rowaaqLms: "نظام رُواق التعليمي",
    rowaaqLmsDesc: "نظام إدارة تعلّم شامل مع عناصر تحفيزية لإدارة دورة حياة الطالب.",
    viewProject: "عرض المشروع",
    viewAllProjects: "عرض جميع المشاريع",
    duration: "المدة",
    teamSize: "حجم الفريق",
    
    // Contact Section
    contactTitle: "تواصل معنا",
    contactTitleHighlight: "فكرتك تبدأ من هنا",
    contactDescription: "الوقت المثالي لتنفيذ فكرتك هو الآن. تواصل مع فريقنا واحنا نحول أفكارك الى مشروع ناجح .",
    getInTouch: "تواصل معنا",
    fullName: "الاسم الكامل",
    emailAddress: "عنوان البريد الإلكتروني",
    phoneNumber: "رقم الهاتف",
    projectType: "نوع المشروع",
    message: "الرسالة",
    selectService: "اختر خدمة",
    mobile: "تطوير تطبيقات الهاتف المحمول",
    web: "تطوير الويب",
    backend: "تطوير الخادم الخلفي",
    ai: "حلول الذكاء الاصطناعي",
    design: "التصميم والعلامة التجارية",
    consultingShort: "استشارات",
    sendMessage: "إرسال الرسالة",
    sending: "جارٍ الإرسال...",
    emailUs: "راسلنا",
    callUs: "اتصل بنا",
    visitUs: "العنوان",
    followUs: "تابعنا",
    namePlaceholder: "احمد محمد",
    emailPlaceholder: "ahmad@example.com",
    phonePlaceholder: "+966 50 123 4567",
    messagePlaceholder: "أخبرنا عن مشروعك...",
    messageSentSuccess: "تم إرسال الرسالة بنجاح!",
    messageSentDescription: "سنرد عليك خلال 24 ساعة.",
    messageFailedTitle: "فشل إرسال الرسالة",
    locationDetails1: "المنصورة, مصر",
    locationDetails2: "طريق الملك فهد، حي العليا",
    
    // Footer
    footerServices: "الخدمات",
    footerCompany: "الشركة",
    footerAboutUs: "حول ابتكاري",
    footerPortfolio: "أعمالنا",
    footerCareers: "الوظائف",
    footerContact: "اتصل بنا",
    footerBlog: "المدونة",
    privacyPolicy: "سياسة الخصوصية",
    termsOfService: "شروط الخدمة",
    allRightsReserved: "جميع الحقوق محفوظة",
    footerDescription: "نحن متخصصون في تطوير التطبيقات متعددة المنصات، والحلول الخلفية، وتطوير الويب، والابتكارات الذكية المتطورة التي تدفع عملك إلى الأمام .",
    footerCopyright: "ابتكاري للتحول الرقمي © 2025 جميع الحقوق محفوظة",
    
    // Modal
    close: "إغلاق",
    technologies: "التقنيات",
    features: "الميزات",
    benefits: "الفوائد",
    requestQuote: "اطلب عرض سعر",

    // Hero Trust Bar
    trustedBy: "موثوق به من قبل الشركات المبتكرة",

    // Services CTA
    viewAllServices: "عرض جميع الخدمات",

    // Newsletter
    newsletterTitle: "اشترك في نشرتنا الإخبارية",
    newsletterDescription: "احصل على آخر الأخبار والتحديثات مباشرة في بريدك",
    subscribe: "اشترك",

    // Footer
    footerGetInTouch: "تواصل معنا",
  },
};

interface LanguageProviderProps {
  readonly children: React.ReactNode;
  readonly language: Language;
  readonly onLanguageChange: (language: Language) => void;
}

export function LanguageProvider({ children, language: currentLanguage, onLanguageChange }: LanguageProviderProps) {
  // Memoize the translation function
  const t = useCallback((key: string): string => {
    const keys = key.split('.');
    let value: any = translations[currentLanguage];
    
    for (const k of keys) {
      if (value && value[k] !== undefined) {
        value = value[k];
      } else {
        console.warn(`Translation key '${key}' not found for language '${currentLanguage}'`);
        return key; // Return the key if translation not found
      }
    }
    
    return typeof value === "string" ? value : key;
  }, [currentLanguage]);

  // Memoize the context value
  const contextValue = useMemo(() => ({
    language: currentLanguage,
    setLanguage: onLanguageChange,
    t
  }), [currentLanguage, onLanguageChange, t]);

  // Update HTML lang and dir attributes when language changes
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = currentLanguage;
      document.documentElement.dir = currentLanguage === 'ar' ? 'rtl' : 'ltr';
      localStorage.setItem('language', currentLanguage);
    }
  }, [currentLanguage]);
  

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
